/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { Icon } from '@/components/Icon';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/stores/uiStore';
import {
  CARD_WIDTH,
  EDGE,
  firstVisible,
  isOnScreen,
  isPhone,
  layoutCard,
  liftAboveSheet,
  spotlightBox,
  type Box,
  type CardLayout,
} from './geometry';
import { resolveTour, type ResolvedTour, type TourStop } from './tourSteps';
import './tour.css';

interface Geometry {
  spot: Box;
  card: CardLayout;
}

interface TourState {
  tour: ResolvedTour;
  index: number;
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Same stops pointing at the same targets: a re-resolve that changed nothing keeps its state. */
const sameTour = (a: ResolvedTour, b: ResolvedTour) =>
  a.kind === b.kind &&
  a.stops.length === b.stops.length &&
  a.stops.every((s, i) => s.id === b.stops[i].id && s.selector === b.stops[i].selector);

const round = (n: number) => Math.round(n * 2) / 2;
const sameGeometry = (a: Geometry | null, b: Geometry) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Puts focus back where the tour was started from. A launcher that has gone away (the Help menu
 * item, the invitation) hands it to the Help button instead, so keyboard users are never dropped
 * at the top of the page.
 */
function restoreFocus(el: HTMLElement | null) {
  if (el && el !== document.body && el.isConnected && isOnScreen(el, false)) {
    el.focus({ preventScroll: true });
    return;
  }
  firstVisible('[data-tour="help"]')?.focus({ preventScroll: true });
}

/** Moves Tab / Shift+Tab around the card's buttons so focus never wanders into the page below. */
function trapTab(e: KeyboardEvent, card: HTMLElement) {
  const buttons = [...card.querySelectorAll<HTMLElement>('button')];
  const first = buttons[0];
  const last = buttons[buttons.length - 1];
  const active = document.activeElement;
  const wrap = e.shiftKey ? active === first || active === card : active === last;
  if (wrap || !card.contains(active)) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  }
}

/**
 * Quick tour: a spotlight on one element at a time with a short card beside it (a sheet at the
 * screen edge on phones and short screens). The tour is built from what is on screen when it
 * starts and rebuilt when the window changes size, so it never points at something hidden and
 * never navigates or opens anything on the user's behalf.
 */
export function TourOverlay() {
  const { t } = useTranslation();
  const requested = useUiStore((s) => s.tourKind);
  const endTour = useUiStore((s) => s.endTour);
  const { pathname } = useLocation();
  const titleId = useId();
  const bodyId = useId();

  // Menus still closing (the Help menu it was started from) do not hide targets; see isOnScreen.
  const [{ tour, index }, setState] = useState<TourState>(() => ({
    tour: resolveTour(requested, firstVisible),
    index: 0,
  }));
  const [geo, setGeo] = useState<Geometry | null>(null);
  // Where focus was when the tour started, read before the card takes it.
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  const cardRef = useRef<HTMLDivElement>(null);
  // The card's height when last laid out as a popover (see layoutCard).
  const popoverHeight = useRef(0);
  const { stops } = tour;
  const total = stops.length;
  const stop = stops[index] as (typeof stops)[number] | undefined;
  const isLast = index === total - 1;

  /** Rebuild the stop list for the current screen, staying on the same stop where it still exists. */
  const refresh = useCallback(() => {
    setState((s) => {
      const next = resolveTour(requested, firstVisible);
      if (sameTour(s.tour, next)) return s;
      const same = next.stops.findIndex((x) => x.id === s.tour.stops[s.index]?.id);
      return { tour: next, index: same >= 0 ? same : Math.min(s.index, next.stops.length - 1) };
    });
  }, [requested]);

  const next = useCallback(() => {
    if (isLast) endTour();
    else setState((s) => ({ ...s, index: s.index + 1 }));
  }, [isLast, endTour]);
  const back = useCallback(() => setState((s) => ({ ...s, index: Math.max(0, s.index - 1) })), []);

  /** Measure the target and card and lay the card out; a no-op when nothing moved. */
  const update = useCallback(() => {
    const card = cardRef.current;
    // The card is rendered exactly when there is a stop; none means an empty tour about to close.
    if (!card) return;
    const { selector, side } = stop as TourStop;
    const el = firstVisible(selector, false);
    if (!el) {
      refresh();
      return;
    }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const r = el.getBoundingClientRect();
    const box = spotlightBox(r, vw, vh);
    const spot = {
      top: round(box.top),
      left: round(box.left),
      width: round(box.width),
      height: round(box.height),
    };
    // The natural width, not the measured one: a card narrowed to fit one stop grows back for the next.
    const size = { width: Math.min(CARD_WIDTH, vw - EDGE * 2), height: card.offsetHeight };
    if (card.classList.contains('tour-card--popover')) popoverHeight.current = size.height;
    const layout = layoutCard(spot, size, popoverHeight.current, vw, vh, side);
    const nextGeo = { spot, card: layout };
    setGeo((prev) => (sameGeometry(prev, nextGeo) ? prev : nextGeo));
  }, [stop, refresh]);

  // Nothing left to show (everything it pointed at went away): close instead of floating empty.
  useEffect(() => {
    if (!stop) endTour();
  }, [stop, endTour]);

  // The tour belongs to the page it was started on; leaving it (browser back) ends it.
  const [startPath] = useState(pathname);
  useEffect(() => {
    if (pathname !== startPath) endTour();
  }, [pathname, startPath, endTour]);

  // New stop: bring the target into view, lay out before paint, and keep focus inside the card
  // (the Back or Skip button that had it may just have been removed).
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const el = firstVisible((stop as TourStop).selector, false);
    if (el) {
      const sheet = isPhone(window.innerWidth);
      el.scrollIntoView({
        block: sheet ? 'center' : 'nearest',
        inline: 'nearest',
        behavior: sheet || reducedMotion() ? 'auto' : 'smooth',
      });
      if (sheet) liftAboveSheet(el, card.offsetHeight);
    }
    update();
    if (!card.contains(document.activeElement)) card.focus({ preventScroll: true });
  }, [stop, update]);

  // Follow the target through scrolling, resizing and late layout shifts.
  useEffect(() => {
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const onResize = () => {
      refresh();
      schedule();
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', schedule, true);
    const observer = new ResizeObserver(schedule);
    if (cardRef.current) observer.observe(cardRef.current);
    const el = stop ? firstVisible(stop.selector, false) : null;
    if (el) observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', schedule, true);
      observer.disconnect();
    };
  }, [stop, update, refresh]);

  // Focus moves into the card and stays there; on close it goes back to where it came from.
  useEffect(() => {
    const returnTo = { current: opener };
    const onFocusIn = (e: FocusEvent) => {
      const card = cardRef.current;
      const target = e.target as HTMLElement;
      if (!card || card.contains(target)) return;
      // Something below (a closing menu returning focus to its trigger) took focus: remember it
      // as the place to return to, and take focus back.
      returnTo.current = target;
      card.focus({ preventScroll: true });
    };
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      restoreFocus(returnTo.current);
    };
  }, [opener]);

  // Keyboard: arrows step, Enter on the card advances, Escape closes. Captured, so the page below
  // (diagram panning, list navigation) never also reacts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const card = cardRef.current;
      if (!card || e.altKey || e.ctrlKey || e.metaKey) return;
      const handled = (fn: () => void) => {
        e.preventDefault();
        e.stopPropagation();
        fn();
      };
      if (e.key === 'Escape') handled(endTour);
      else if (e.key === 'ArrowRight') handled(next);
      else if (e.key === 'ArrowLeft') handled(back);
      else if (e.key === 'Enter' && e.target === card) handled(next);
      else if (e.key === 'Tab') trapTab(e, card);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [next, back, endTour]);

  if (!stop) return null;

  const card = geo?.card;
  // Before the first measurement, pick the mode from the window so the card renders at its real
  // size (a sheet is wider than a popover) and is measured correctly.
  const sheet = card ? card.kind === 'sheet' : isPhone(window.innerWidth);
  const dock = card?.kind === 'sheet' ? card.dock : 'bottom';
  const popover = card?.kind === 'popover' ? card : null;
  const page = t(`tour.page.${tour.kind}`);
  const count = t('tour.stepCount', { current: index + 1, total });

  return (
    <div className="tour-root">
      {/* Keeps the page inert while the tour runs; mousedown is swallowed so focus stays put. */}
      <div className="tour-shield" onMouseDown={(e) => e.preventDefault()} />
      {geo && (
        <div
          className="tour-spot"
          style={{
            top: geo.spot.top,
            left: geo.spot.left,
            width: geo.spot.width,
            height: geo.spot.height,
          }}
        />
      )}

      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        data-ready={geo ? '' : undefined}
        className={cn(
          'tour-card',
          sheet ? `tour-card--sheet tour-card--dock-${dock}` : 'tour-card--popover',
          popover?.side && `tour-card--${popover.side}`,
        )}
        style={
          popover
            ? ({
                top: popover.top,
                left: popover.left,
                width: popover.width,
                '--tour-arrow': `${popover.arrow}px`,
              } as CSSProperties)
            : undefined
        }
      >
        {popover?.side && <span className="tour-arrow" aria-hidden="true" />}
        <div className="tour-head">
          <span className="tour-eyebrow">{t('tour.eyebrow', { page })}</span>
          {!isLast && (
            <button type="button" className="tour-skip" onClick={endTour}>
              {t('tour.skip')}
            </button>
          )}
        </div>
        <div key={stop.id} className="tour-content">
          <h2 id={titleId} className="tour-title">
            {t(stop.title)}
          </h2>
          <p id={bodyId} className="tour-body">
            {t(stop.body)}
          </p>
        </div>
        <div className="tour-foot">
          <div className="tour-progress">
            <span className="tour-dots" aria-hidden="true">
              {stops.map((s, i) => (
                <span
                  key={s.id}
                  className={cn('tour-dot', i === index && 'is-on', i < index && 'is-done')}
                />
              ))}
            </span>
            <span className="tour-count">{count}</span>
          </div>
          <div className="tour-actions">
            {index > 0 && (
              <button type="button" className="tour-btn tour-btn--secondary" onClick={back}>
                <Icon name="chevl" size={16} />
                {t('tour.back')}
              </button>
            )}
            <button type="button" className="tour-btn tour-btn--primary" onClick={next}>
              {isLast ? t('tour.done') : t('tour.next')}
              <Icon name={isLast ? 'check' : 'chevr'} size={16} />
            </button>
          </div>
        </div>
      </div>

      <p className="sr-only" aria-live="polite">
        {t('tour.announce', { count, title: t(stop.title) })}
      </p>
    </div>
  );
}
